import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, Heart, Activity, BookOpen, Briefcase, TrendingUp, AlertTriangle } from 'lucide-react';
import THRDistrictDetailModal from '../../components/THRDistrictDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#6366f1'];

const PURNADashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // State for all data
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [enrollmentByDistrict, setEnrollmentByDistrict] = useState([]);
  const [snpData, setSnpData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [anemiaData, setAnemiaData] = useState(null);
  const [nutritionData, setNutritionData] = useState(null);
  const [counsellingData, setCounsellingData] = useState(null);
  const [empowermentData, setEmpowermentData] = useState(null);
  const [topDistricts, setTopDistricts] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        enrollment,
        enrollmentDist,
        snp,
        health,
        anemia,
        nutrition,
        counselling,
        empowerment,
        top,
        attention
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboards/purna/enrollment-overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/enrollment-by-district`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/snp-coverage`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/health-services-overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/anemia-status-overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/nutritional-status-overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/counselling-services-overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/empowerment-overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/top-performing-districts?limit=10`),
        axios.get(`${BACKEND_URL}/api/dashboards/purna/districts-needing-attention?limit=10`)
      ]);

      setEnrollmentData(enrollment.data);
      setEnrollmentByDistrict(enrollmentDist.data);
      setSnpData(snp.data);
      setHealthData(health.data);
      setAnemiaData(anemia.data);
      setNutritionData(nutrition.data);
      setCounsellingData(counselling.data);
      setEmpowermentData(empowerment.data);
      setTopDistricts(top.data);
      setNeedsAttention(attention.data);
    } catch (error) {
      console.error('Error fetching PURNA data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading PURNA Dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 pt-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">PURNA & Poshan Sangam Dashboard</h1>
          <p className="text-gray-600">Adolescent Girls Health, Nutrition & Empowerment Program - October 2025</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="overview">Program Overview</TabsTrigger>
            <TabsTrigger value="health">Health & Nutrition</TabsTrigger>
            <TabsTrigger value="counselling">Counselling Services</TabsTrigger>
            <TabsTrigger value="empowerment">Empowerment</TabsTrigger>
          </TabsList>

          {/* Tab 1: Program Overview */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Surveyed</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {enrollmentData?.total_surveyed?.toLocaleString()}
                      </p>
                    </div>
                    <Users className="h-8 w-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Enrolled</p>
                      <p className="text-2xl font-bold text-green-600">
                        {enrollmentData?.total_enrolled?.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {enrollmentData?.enrollment_pct}% coverage
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Districts Covered</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {enrollmentData?.districts_count}
                      </p>
                    </div>
                    <Activity className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">SNP Coverage</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {snpData?.snp_coverage_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {snpData?.total_received_snp?.toLocaleString()} girls
                      </p>
                    </div>
                    <Heart className="h-8 w-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enrollment by District */}
            <Card>
              <CardHeader>
                <CardTitle>Enrollment Coverage by District (Top 15)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={enrollmentByDistrict.slice(0, 15)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="district_id" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      tick={{ fontSize: 11 }}
                      interval={0}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      width={60}
                    />
                    <Tooltip 
                      contentStyle={{ fontSize: '12px' }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '10px' }}
                      iconSize={12}
                      style={{ fontSize: '12px' }}
                    />
                    <Bar 
                      dataKey="enrolled" 
                      fill="#8b5cf6" 
                      name="Enrolled Girls"
                      shape={(props) => {
                        const { payload, x, y, width, height } = props;
                        const [isHovered, setIsHovered] = React.useState(false);
                        
                        const handleClick = (e) => {
                          e.stopPropagation();
                          if (payload?.district_id || payload?.district) {
                            const districtId = payload.district_id || payload.district;
                            setSelectedDistrict({ district: districtId, district_id: districtId });
                            setIsModalOpen(true);
                          }
                        };

                        return (
                          <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            fill={isHovered ? '#7c3aed' : '#8b5cf6'}
                            style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                            onClick={handleClick}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                          />
                        );
                      }}
                      cursor="pointer"
                    />
                    <Bar 
                      dataKey="surveyed" 
                      fill="#cbd5e1" 
                      name="Total Surveyed"
                      shape={(props) => {
                        const { payload, x, y, width, height } = props;
                        const [isHovered, setIsHovered] = React.useState(false);
                        
                        const handleClick = (e) => {
                          e.stopPropagation();
                          if (payload?.district_id || payload?.district) {
                            const districtId = payload.district_id || payload.district;
                            setSelectedDistrict({ district: districtId, district_id: districtId });
                            setIsModalOpen(true);
                          }
                        };

                        return (
                          <rect
                            x={x}
                            y={y}
                            width={width}
                            height={height}
                            fill={isHovered ? '#94a3b8' : '#cbd5e1'}
                            style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                            onClick={handleClick}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                          />
                        );
                      }}
                      cursor="pointer"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Performance Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Top Performing Districts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topDistricts.slice(0, 5).map((district, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                        onClick={() => {
                          setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                          setIsModalOpen(true);
                        }}
                      >
                        <div>
                          <p className="font-medium text-gray-900">{district.district_id}</p>
                          <p className="text-xs text-gray-600">
                            Enrollment: {district.enrollment_pct}% | SNP: {district.snp_pct}%
                          </p>
                        </div>
                        <div className="text-green-600 font-bold text-lg">#{index + 1}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    Districts Needing Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {needsAttention.slice(0, 5).map((district, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                        onClick={() => {
                          setSelectedDistrict({ district: district.district_id, district_id: district.district_id });
                          setIsModalOpen(true);
                        }}
                      >
                        <div>
                          <p className="font-medium text-gray-900">{district.district_id}</p>
                          <p className="text-xs text-gray-600">
                            Enrollment: {district.enrollment_pct}% | IFA: {district.ifa_pct}%
                          </p>
                        </div>
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab 2: Health & Nutrition */}
          <TabsContent value="health" className="space-y-6">
            {/* Health Services Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">IFA Tablets Distribution</p>
                    <p className="text-3xl font-bold text-blue-600">{healthData?.ifa_coverage_pct}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {healthData?.received_ifa?.toLocaleString()} girls received
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">IFA Consumption Rate</p>
                    <p className="text-3xl font-bold text-green-600">{healthData?.ifa_consumption_pct}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {healthData?.consumed_ifa?.toLocaleString()} consuming regularly
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">Health Checkup Coverage</p>
                    <p className="text-3xl font-bold text-purple-600">{healthData?.health_checkup_pct}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {healthData?.health_checkup?.toLocaleString()} girls checked
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Anemia Status */}
            <Card>
              <CardHeader>
                <CardTitle>Anemia Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={[
                            { name: 'Normal (>11 gm/dl)', value: anemiaData?.normal || 0, color: COLORS[3] },
                            { name: 'Mild (9-11 gm/dl)', value: anemiaData?.mild || 0, color: COLORS[2] },
                            { name: 'Moderate (7-9 gm/dl)', value: anemiaData?.moderate || 0, color: COLORS[1] },
                            { name: 'Severe (<7 gm/dl)', value: anemiaData?.severe || 0, color: COLORS[5] }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => {
                            const percent = anemiaData?.total_tested > 0 ? ((entry.value / anemiaData.total_tested) * 100) : 0;
                            // Only show label if percentage is >= 5% to avoid overlapping
                            if (percent >= 5) {
                              return `${entry.name.split(' ')[0]}: ${percent.toFixed(1)}%`;
                            }
                            return '';
                          }}
                          outerRadius={90}
                          innerRadius={0}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[
                            { name: 'Normal', value: anemiaData?.normal || 0 },
                            { name: 'Mild', value: anemiaData?.mild || 0 },
                            { name: 'Moderate', value: anemiaData?.moderate || 0 },
                            { name: 'Severe', value: anemiaData?.severe || 0 }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconSize={12}
                          style={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Total HB Testing Coverage</p>
                      <p className="text-2xl font-bold text-gray-900">{anemiaData?.testing_coverage_pct}%</p>
                      <p className="text-xs text-gray-500">{anemiaData?.total_tested?.toLocaleString()} girls tested</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-xs text-gray-600">Severe Anemia</p>
                        <p className="text-xl font-bold text-red-600">{anemiaData?.severe_pct}%</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <p className="text-xs text-gray-600">Moderate</p>
                        <p className="text-xl font-bold text-orange-600">{anemiaData?.moderate_pct}%</p>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded-lg">
                        <p className="text-xs text-gray-600">Mild</p>
                        <p className="text-xl font-bold text-yellow-600">{anemiaData?.mild_pct}%</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-gray-600">Normal</p>
                        <p className="text-xl font-bold text-green-600">{anemiaData?.normal_pct}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Nutritional Status (BMI) */}
            <Card>
              <CardHeader>
                <CardTitle>Nutritional Status (BMI Assessment)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          { category: 'Underweight', count: nutritionData?.underweight || 0, pct: nutritionData?.underweight_pct || 0 },
                          { category: 'Normal', count: nutritionData?.normal || 0, pct: nutritionData?.normal_pct || 0 },
                          { category: 'Overweight', count: nutritionData?.overweight || 0, pct: nutritionData?.overweight_pct || 0 },
                          { category: 'Obese', count: nutritionData?.obese || 0, pct: nutritionData?.obese_pct || 0 }
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8b5cf6" name="Number of Girls" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">BMI Assessment Coverage</p>
                      <p className="text-2xl font-bold text-gray-900">{nutritionData?.assessment_coverage_pct}%</p>
                      <p className="text-xs text-gray-500">{nutritionData?.total_assessed?.toLocaleString()} girls assessed</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                        <span className="text-sm text-gray-700">Underweight (&lt;18.5)</span>
                        <span className="font-bold text-blue-600">{nutritionData?.underweight_pct}%</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                        <span className="text-sm text-gray-700">Normal (18.5-25)</span>
                        <span className="font-bold text-green-600">{nutritionData?.normal_pct}%</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                        <span className="text-sm text-gray-700">Overweight (25-30)</span>
                        <span className="font-bold text-orange-600">{nutritionData?.overweight_pct}%</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                        <span className="text-sm text-gray-700">Obese (&gt;30)</span>
                        <span className="font-bold text-red-600">{nutritionData?.obese_pct}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Counselling Services */}
          <TabsContent value="counselling" className="space-y-6">
            {/* Counselling Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <BookOpen className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-600 mb-1">NHE Counselling</p>
                    <p className="text-2xl font-bold text-blue-600">{counsellingData?.nhe_pct}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {counsellingData?.nhe?.toLocaleString()} girls
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Heart className="h-8 w-8 text-pink-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-600 mb-1">ARSH Services</p>
                    <p className="text-2xl font-bold text-pink-600">{counsellingData?.arsh_pct}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {counsellingData?.arsh?.toLocaleString()} girls
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Activity className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-600 mb-1">Life Skills Education</p>
                    <p className="text-2xl font-bold text-purple-600">{counsellingData?.life_skills_pct}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {counsellingData?.life_skills?.toLocaleString()} girls
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <BookOpen className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-600 mb-1">Legal Rights Info</p>
                    <p className="text-2xl font-bold text-green-600">{counsellingData?.legal_rights_pct}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {counsellingData?.legal_rights?.toLocaleString()} girls
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Counselling Coverage Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Counselling Services Coverage Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={[
                      { service: 'NHE', coverage: counsellingData?.nhe_pct || 0, count: counsellingData?.nhe || 0 },
                      { service: 'ARSH', coverage: counsellingData?.arsh_pct || 0, count: counsellingData?.arsh || 0 },
                      { service: 'Life Skills', coverage: counsellingData?.life_skills_pct || 0, count: counsellingData?.life_skills || 0 },
                      { service: 'Legal Rights', coverage: counsellingData?.legal_rights_pct || 0, count: counsellingData?.legal_rights || 0 }
                    ]}
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      domain={[0, 100]} 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      dataKey="service" 
                      type="category" 
                      width={110}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ fontSize: '12px' }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '10px' }}
                      iconSize={12}
                      style={{ fontSize: '12px' }}
                    />
                    <Bar dataKey="coverage" fill="#8b5cf6" name="Coverage %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Service Details */}
            <Card>
              <CardHeader>
                <CardTitle>Counselling Service Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Nutrition & Health Education (NHE)</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Comprehensive nutrition and health awareness sessions covering dietary practices, personal hygiene, and wellness.
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Girls Counselled:</span>
                      <span className="text-lg font-bold text-blue-600">
                        {counsellingData?.nhe?.toLocaleString()} ({counsellingData?.nhe_pct}%)
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-pink-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">ARSH, Family Welfare & Child Care</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Adolescent Reproductive & Sexual Health education including family welfare and child care services.
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Girls Counselled:</span>
                      <span className="text-lg font-bold text-pink-600">
                        {counsellingData?.arsh?.toLocaleString()} ({counsellingData?.arsh_pct}%)
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Life Skills Education (LSE)</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Training on essential life skills including communication, decision-making, and problem-solving abilities.
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Girls Trained:</span>
                      <span className="text-lg font-bold text-purple-600">
                        {counsellingData?.life_skills?.toLocaleString()} ({counsellingData?.life_skills_pct}%)
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Legal Rights Information</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Education on legal rights, child protection, and awareness about laws concerning adolescent girls.
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Girls Informed:</span>
                      <span className="text-lg font-bold text-green-600">
                        {counsellingData?.legal_rights?.toLocaleString()} ({counsellingData?.legal_rights_pct}%)
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 4: Empowerment */}
          <TabsContent value="empowerment" className="space-y-6">
            {/* Empowerment Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Vocational Training</p>
                      <p className="text-3xl font-bold text-purple-600">{empowermentData?.vocational_pct}%</p>
                      <p className="text-sm text-gray-500 mt-2">
                        {empowermentData?.vocational_training?.toLocaleString()} girls enrolled in vocational training programs
                      </p>
                    </div>
                    <Briefcase className="h-12 w-12 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Public Service Visits</p>
                      <p className="text-3xl font-bold text-blue-600">{empowermentData?.public_visits_pct}%</p>
                      <p className="text-sm text-gray-500 mt-2">
                        {empowermentData?.public_visits?.toLocaleString()} girls participated in public service visits
                      </p>
                    </div>
                    <Users className="h-12 w-12 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Empowerment Overview Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Empowerment Initiatives Coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { 
                        initiative: 'Vocational Training',
                        enrolled: empowermentData?.vocational_training || 0,
                        percentage: empowermentData?.vocational_pct || 0
                      },
                      {
                        initiative: 'Public Visits',
                        enrolled: empowermentData?.public_visits || 0,
                        percentage: empowermentData?.public_visits_pct || 0
                      }
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="initiative" />
                    <YAxis yAxisId="left" orientation="left" stroke="#8b5cf6" />
                    <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="enrolled" fill="#8b5cf6" name="Girls Enrolled" />
                    <Bar yAxisId="right" dataKey="percentage" fill="#3b82f6" name="Coverage %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Program Impact */}
            <Card>
              <CardHeader>
                <CardTitle>Empowerment Program Impact</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg">
                    <div className="flex items-start gap-4">
                      <Briefcase className="h-10 w-10 text-purple-600 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Vocational Training Programs</h3>
                        <p className="text-gray-700 mb-4">
                          Skill development initiatives providing adolescent girls with practical vocational skills for employment and entrepreneurship.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Total Enrolled</p>
                            <p className="text-2xl font-bold text-purple-600">
                              {empowermentData?.vocational_training?.toLocaleString()}
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Coverage Rate</p>
                            <p className="text-2xl font-bold text-purple-600">
                              {empowermentData?.vocational_pct}%
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Program Base</p>
                            <p className="text-2xl font-bold text-gray-700">
                              {empowermentData?.total_enrolled?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
                    <div className="flex items-start gap-4">
                      <Users className="h-10 w-10 text-blue-600 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Public Service Visits</h3>
                        <p className="text-gray-700 mb-4">
                          Organized visits to public service locations, government offices, and institutions to enhance civic awareness and social engagement.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Girls Participated</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {empowermentData?.public_visits?.toLocaleString()}
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Participation Rate</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {empowermentData?.public_visits_pct}%
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600">Total Enrolled Base</p>
                            <p className="text-2xl font-bold text-gray-700">
                              {empowermentData?.total_enrolled?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Outcomes */}
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Outcomes & Benefits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">🎓 Skill Development</h4>
                        <p className="text-sm text-gray-600">
                          Girls gain practical skills in various trades enhancing their employability and economic independence.
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">🤝 Social Engagement</h4>
                        <p className="text-sm text-gray-600">
                          Public visits increase civic awareness and help girls understand governmental systems and services.
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">💪 Empowerment</h4>
                        <p className="text-sm text-gray-600">
                          Programs boost confidence, leadership abilities, and decision-making skills among adolescent girls.
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">📈 Future Opportunities</h4>
                        <p className="text-sm text-gray-600">
                          Vocational training opens pathways to entrepreneurship, self-employment, and career advancement.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>

    {/* District Detail Modal */}
    <THRDistrictDetailModal
      district={selectedDistrict}
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        setSelectedDistrict(null);
      }}
      reportingPeriod="Oct-2025"
      dashboardType="purna"
    />
    </div>
  );
};

export default PURNADashboard;
